/** @typedef {import('@/types/tracking').TrackingData} TrackingData */

import { injectScript } from '@/utils/inject-script.js'

import { logError } from '@/utils/error-handlers'

// NOTE: this block is a slightly modified version of the Segment Quickstart Guide:
export default {
  allowTracking: false,

  /**
   * Segment's page tracking.
   * https://segment.com/docs/connections/spec/page/
   *
   * @param {TrackingData} data object containing relevant navigation info.
   */
  page(data) {
    if (!this.allowTracking) return
    if (!window.analytics?.page) return
    if (!data.title) return

    const title = data.title
    window.analytics.page(title, data)
  },

  // Inspired by: https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/quickstart/
  install(installParams) {
    if (!this.allowTracking) return

    const writeKey = installParams.segmentKey

    // Create a queue, but don't obliterate an existing one!
    const analytics = (window.analytics = window.analytics || [])

    // If the real analytics.js is already on the page return.
    if (analytics.initialize) return

    // If the snippet was invoked already show an error.
    if (analytics.invoked) {
      throw new Error('Segment snippet included twice.')
    }

    // Invoked flag, to make sure the snippet
    // is never invoked twice.
    analytics.invoked = true

    // A list of the methods in Analytics.js to stub.
    analytics.methods = [
      'trackSubmit',
      'trackClick',
      'trackLink',
      'trackForm',
      'pageview',
      'identify',
      'reset',
      'group',
      'track',
      'ready',
      'alias',
      'debug',
      'page',
      'once',
      'off',
      'on',
    ]

    // Define a factory to create stubs. These are placeholders
    // for methods in Analytics.js so that you never have to wait
    // for it to load to actually record data. The `method` is
    // stored as the first argument, so we can replay the data.
    analytics.factory = function (method) {
      return function () {
        const args = Array.prototype.slice.call(arguments)
        args.unshift(method)
        analytics.push(args)
        return analytics
      }
    }

    // For each of our methods, generate a queueing stub.
    analytics.methods.forEach(function (key) {
      analytics[key] = analytics.factory(key)
    })

    // Define a method to load Analytics.js from our CDN,
    // and that will be sure to only ever load it once.
    analytics.load = async function (key) {
      try {
        await injectScript({ src: `https://cdn.segment.com/analytics.js/v1/${key}/analytics.min.js`, async: '' })
      } catch (error) {
        // Explicitly ignore script injection errors for Segment as this is non-Actionable for now
      }
    }

    // Add a version to keep track of what's in the wild.
    analytics.SNIPPET_VERSION = '4.1.0'

    // Load Analytics.js with your key, which will automatically
    // load the tools you've enabled for your account. Boosh!
    analytics.load(writeKey)
  },

  // Step 2: identify
  // https://segment.com/docs/connections/spec/identify/
  identify(user, traits) {
    if (!this.allowTracking) return
    if (!window.Appcues) return

    try {
      window.analytics.identify(
        user.id,
        {
          email: user.email,
          name: user.company_name,
          created_at: user.date_joined_unix,
          ...traits,
        },
        {
          Intercom: {
            user_hash: user.intercom_hash,
          },
        },
      )
    } catch (error) {
      logError(error)
    }
  },

  track(eventName, eventProperties, options) {
    if (!this.allowTracking) return
    if (!window.Appcues) return

    try {
      window.analytics.track(eventName, eventProperties, options)
    } catch (error) {
      logError(error)
    }
  },
}
